/**
 * MSAL.js helpers for Entra silent SSO.
 * Configuration is fetched at runtime from /api/auth/entra/config.
 */
import { PublicClientApplication, BrowserAuthError } from "@azure/msal-browser";

let _msal = null;
let _cfg = null;

// Detect if we're running inside a hidden iframe (MSAL silent SSO iframe).
// When true, our React app should NOT render — MSAL handles the response internally.
export function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true; // cross-origin frame — treat as iframe
  }
}

export async function loadEntraConfig(api) {
  if (_cfg !== null) return _cfg;
  try {
    const { data } = await api.get("/auth/entra/config");
    _cfg = data.enabled ? data : { enabled: false };
  } catch {
    _cfg = { enabled: false };
  }
  return _cfg;
}

function buildMsal(cfg) {
  if (_msal) return _msal;
  _msal = new PublicClientApplication({
    auth: {
      clientId: cfg.client_id,
      authority: `https://login.microsoftonline.com/${cfg.tenant_id || "common"}`,
      // ALWAYS use the SPA callback route — the stored redirect_uri in the admin
      // panel is only informational (must also be whitelisted in Entra portal).
      redirectUri: `${window.location.origin}/auth/entra/callback`,
      postLogoutRedirectUri: `${window.location.origin}/login`,
      // NOTE: `navigateToLoginRequestUrl` was REMOVED from BrowserAuthOptions in
      // MSAL v5. It must now be passed to each `handleRedirectPromise()` call.
    },
    cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
  });
  return _msal;
}

// Clear any leftover MSAL "interaction_in_progress" lock — safe to call any time.
function clearMsalInteractionLock() {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("msal.") && k.includes("interaction.status"))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (_) { /* ignore storage errors */ }
}

export async function attemptSilentSSO(api) {
  const cfg = await loadEntraConfig(api);
  if (!cfg.enabled) return { silent: false, reason: "disabled" };

  const msal = buildMsal(cfg);
  await msal.initialize();
  // Always process any pending redirect first — this releases MSAL's interaction lock.
  // In MSAL v5, `navigateToLoginRequestUrl` MUST be passed here (not in config).
  try { await msal.handleRedirectPromise({ hash: window.location.hash, navigateToLoginRequestUrl: false }); } catch (_) { /* ignore */ }

  try {
    const result = await msal.ssoSilent({
      scopes: ["openid", "profile", "email"],
      loginHint: undefined,
      // Cap the wait to ~2s. Default is 6s which feels like a hang for anyone
      // who isn't signed into a Microsoft session in this browser.
      iframeHashTimeout: 2000,
      loadFrameTimeout: 2000,
    });
    return { silent: true, idToken: result.idToken, account: result.account };
  } catch (e) {
    return { silent: false, reason: e?.errorCode || e?.message || "interaction_required" };
  }
}

export async function loginWithEntraRedirect(api) {
  const cfg = await loadEntraConfig(api);
  if (!cfg.enabled) throw new Error("Entra not enabled");
  const msal = buildMsal(cfg);
  await msal.initialize();
  // Drain any prior redirect state / release the interaction lock before we start.
  try { await msal.handleRedirectPromise({ hash: window.location.hash, navigateToLoginRequestUrl: false }); } catch (_) { /* ignore */ }

  const tryLogin = () =>
    msal.loginRedirect({
      scopes: ["openid", "profile", "email"],
      prompt: "select_account",
    });

  try {
    await tryLogin();
  } catch (e) {
    // Recover from a stuck "interaction_in_progress" flag.
    if (e instanceof BrowserAuthError && e.errorCode === "interaction_in_progress") {
      clearMsalInteractionLock();
      await tryLogin();
    } else {
      throw e;
    }
  }
}

export async function handleRedirectResult() {
  if (!_msal) {
    if (!_cfg || !_cfg.enabled) return { result: null, error: null };
    buildMsal(_cfg);
  }
  await _msal.initialize();
  try {
    // MSAL v5: options object with hash + navigateToLoginRequestUrl.
    // Keep the browser on /auth/entra/callback so our React handler can POST
    // the id_token to the backend before any navigation happens.
    const result = await _msal.handleRedirectPromise({
      hash: window.location.hash,
      navigateToLoginRequestUrl: false,
    });
    return { result, error: null };
  } catch (e) {
    // Surface MSAL errors instead of swallowing them so the UI can show what
    // went wrong instead of falling through to a doomed server-side code exchange.
    return {
      result: null,
      error: {
        code: e?.errorCode || e?.name || "msal_error",
        message: e?.errorMessage || e?.message || String(e),
      },
    };
  }
}

export function getCachedCfg() { return _cfg; }
